import { useEffect, useMemo, useState } from "react";
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormGroup, FormLabel,
  MenuItem, TextField, Typography,
} from "@mui/material";
import { IQuoteLineItem, IQuoteProduct, QuoteTerm } from "../../shared/types";
import { EditorSection, sectionDisplayName } from "../QuoteSectionBar/sectionUtils";
import { CopyTermDestination, TermCopyPlan, planTermCopy, quoteTerms, termsInSection } from "./copyTermUtils";
import "./CopyTermDialog.scss";

type Props = {
  open: boolean;
  sections: EditorSection[];
  activeIndex: number;
  productRows: IQuoteProduct[];
  onClose: () => void;
  onCopy: (plan: TermCopyPlan) => void;
};

const newSectionValue = "new";
const lineLabel = (count: number) => `${count} ${count === 1 ? "line" : "lines"}`;

const LineList = ({ title, items }: { title: string; items: IQuoteLineItem[] }) => items.length === 0 ? null : <Box className="copy-term-group">
  <Typography variant="body2" className="copy-term-group-title">{title}</Typography>
  <ul className="copy-term-lines">{items.map((item, index) => <li key={`${item.product_id}-${item.sku_id}-${index}`}>{item.sku_title || item.product_id}</li>)}</ul>
</Box>;

// Issue 52: Copy Term. Picks a source term in the active section, one or more target terms and a
// destination, and previews - before anything changes - what will be copied and what won't.
const CopyTermDialog = ({ open, sections, activeIndex, productRows, onClose, onCopy }: Props) => {
  const section = sections[activeIndex];
  const sourceTerms = useMemo(() => termsInSection(section), [section]);
  const [sourceTerm, setSourceTerm] = useState<QuoteTerm | "">("");
  const [targetTerms, setTargetTerms] = useState<QuoteTerm[]>([]);
  const [destinationValue, setDestinationValue] = useState(String(activeIndex));

  // Each opening starts fresh: the first term present, the next term along checked, this section.
  useEffect(() => {
    if (!open) return;
    const first = sourceTerms[0] || "";
    setSourceTerm(first);
    setTargetTerms(first ? [quoteTerms.find(term => term !== first)!] : []);
    setDestinationValue(String(activeIndex));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const destination: CopyTermDestination = destinationValue === newSectionValue
    ? { kind: "new" } : { kind: "section", index: Number(destinationValue) };
  const plan = useMemo(() => sourceTerm ? planTermCopy({
    sections, sourceIndex: activeIndex, sourceTerm, targetTerms, destination, productRows,
  }) : null, [sections, activeIndex, sourceTerm, targetTerms, destinationValue, productRows]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeSource = (term: QuoteTerm) => {
    setSourceTerm(term);
    setTargetTerms(current => {
      const kept = current.filter(item => item !== term);
      return kept.length ? kept : [quoteTerms.find(item => item !== term)!];
    });
  };
  const toggleTarget = (term: QuoteTerm, checked: boolean) => setTargetTerms(current =>
    checked ? quoteTerms.filter(item => item === term || current.includes(item)) : current.filter(item => item !== term));
  const sourceCount = (term: QuoteTerm) => (section?.items || []).filter(item => item.term_duration === term).length;

  return <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
    <DialogTitle>Copy Term</DialogTitle>
    <DialogContent dividers className="copy-term-dialog">
      {sourceTerms.length === 0 ? <Alert severity="info">This section has no complete lines to copy yet.</Alert> : <>
        <Typography variant="body2" color="text.secondary">
          Copy every line of one term into another term, to offer the same products as a comparison.
        </Typography>
        <Box className="copy-term-fields">
          <TextField select size="small" label="Copy from" value={sourceTerm} className="quick-quote-select"
            inputProps={{ "aria-label": "Copy from term" }} onChange={event => changeSource(event.target.value as QuoteTerm)}>
            {sourceTerms.map(term => <MenuItem key={term} value={term}>{term} ({lineLabel(sourceCount(term))})</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Destination" value={destinationValue} className="quick-quote-select"
            inputProps={{ "aria-label": "Destination section" }} onChange={event => setDestinationValue(event.target.value)}>
            {sections.map((item, index) => <MenuItem key={item.key} value={String(index)}>
              {index === activeIndex ? `This section (${sectionDisplayName(item.name, index)})` : sectionDisplayName(item.name, index)}
            </MenuItem>)}
            <MenuItem value={newSectionValue}>New section</MenuItem>
          </TextField>
        </Box>
        <FormLabel component="legend" className="copy-term-legend">Copy to</FormLabel>
        <FormGroup row>
          {quoteTerms.filter(term => term !== sourceTerm).map(term => <FormControlLabel key={term} label={term}
            control={<Checkbox checked={targetTerms.includes(term)} onChange={event => toggleTarget(term, event.target.checked)} />} />)}
        </FormGroup>
        {targetTerms.length === 0 && <Typography variant="body2" color="error">Choose at least one term to copy to.</Typography>}

        {plan && plan.incompleteCount > 0 && <Alert severity="info" className="copy-term-alert">
          {lineLabel(plan.incompleteCount)} in {plan.sourceTerm} {plan.incompleteCount === 1 ? "is" : "are"} not complete and will be skipped.
        </Alert>}
        {plan?.byTerm.map(result => <Box key={result.term} className="copy-term-result" aria-label={`${result.term} preview`}>
          <Typography variant="subtitle2">{result.term}: {lineLabel(result.copies.length)} will be copied</Typography>
          <LineList title={`Not offered in ${result.term} - not copied`} items={result.notOffered} />
          <LineList title={`Already in ${result.term} in the destination - not copied`} items={result.alreadyPresent} />
          <LineList title={`Manual price - enter the ${result.term} MSRP and Cost after copying (prices update once every line is complete)`} items={result.manualBlanked} />
          <LineList title={`Promotion not carried over - the ${result.term} promotion check runs after copying`} items={result.promoDropped} />
        </Box>)}
      </>}
    </DialogContent>
    <DialogActions>
      <Button color="inherit" onClick={onClose}>Cancel</Button>
      <Button variant="contained" disabled={!plan || plan.copyCount === 0} onClick={() => plan && onCopy(plan)}>
        {plan && plan.copyCount > 0 ? `Copy ${plan.copyCount} ${plan.copyCount === 1 ? "Line" : "Lines"}` : "Copy"}
      </Button>
    </DialogActions>
  </Dialog>;
};

export default CopyTermDialog;
